"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import {
  FaEnvelope,
  FaInstagram,
  FaPhone,
  FaTwitter,
  FaWhatsapp,
} from "react-icons/fa";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { SiteDetail } from "@/interfaces/site-model";
import { Link } from "@/i18n/navigation";
import { PaymentOptions } from "@/utils/payment-options";
import { WhatsAppNotificationForm } from "../whatsapp-notification-form";
import { useParams } from "next/navigation";
import { IService } from "@/interfaces/services/service.interface";

const siteMenu = [
  {
    path: "/",
    label: "home",
  },
  {
    path: "/about-us",
    label: "about us",
  },
  // {
  //   path: "/our-services",
  //   label: "our services",
  // },
  {
    path: "/our-offers",
    label: "our offers",
  },
  {
    path: "/our-blogs",
    label: "our blogs",
  },
  {
    path: "/terms-and-conditions",
    label: "tc",
  },
  {
    path: "/faq",
    label: "faq",
  },
  {
    path: "/contact-us",
    label: "contact us",
  },
];

interface FooterProps {
  data: SiteDetail;
  services: IService[];
}

const Footer = ({ data, services }: FooterProps) => {
  const t = useTranslations("footer");
  const { locale } = useParams();

  let address = `${data?.address?.address_line}, ${data?.address?.city}, ${data?.address?.country}`;

  const mainServices = (services ?? [])
    .map((service: IService) => service)
    .sort((a: any, b: any) => a?.order - b?.order);

  return (
    <footer className="bg-[#0f0f0f] pt-20 px-0 sm:pt-16 pb-10">
      <div className="container px-5 mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Top Bar Section */}
        <div className="flex flex-col justify-start items-start mb-5 md:mb-10 gap-12 sm:mb-12">
          {/* Logo Section */}
          <div className="relative w-[166px] h-[122px]">
            <Image
              src={data?.site_logo?.url}
              alt={data?.site_logo?.alternativeText || "logo"}
              fill
            />
          </div>

          {/* Contact Info Section */}
          <div className="flex flex-col items-start gap-5">
            <Link
              href={`mailto:${data?.contact_details?.email}`}
              className="flex items-center gap-4 text-white"
            >
              <FaEnvelope className="shrink-0" />
              <span>{data?.contact_details?.email}</span>
            </Link>
            <Link
              href={`tel:${data?.contact_details?.phone_number}`}
              className="flex items-center gap-4 text-white"
            >
              <FaPhone className="shrink-0" />
              <span>{data?.contact_details?.phone_number}</span>
            </Link>
            <p className="flex items-center gap-4 text-white">
              <HiOutlineLocationMarker className="shrink-0" />
              <span>{address}</span>
            </p>
          </div>
        </div>
        <div className="mb-5 md:mb-10">
          <h3 className="text-xl font-medium mb-8 text-white">
            {locale === "en" ? "Quick Links" : "الروابط السريعة"}
          </h3>
          {/* Site Menu Section */}
          <nav className="flex flex-col justify-start items-start flex-wrap gap-x-6 gap-y-4 mb-10">
            {siteMenu.map((menu, index) => {
              return (
                <Link
                  href={menu.path}
                  key={index}
                  className="capitalize text-white hover:text-gray-300 transition-colors"
                >
                  {t(menu.label)}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Services Section */}
        <div className="mb-5 md:mb-10">
          <h3 className="text-xl font-medium mb-8 text-white">
            {locale === "en" ? "Our Services" : "الخدمات"}
          </h3>
          <nav className="flex flex-col gap-4">
            {mainServices.map((service, index) => {
              return (
                <Link
                  href={`/our-services/${service?.slug}`}
                  key={index}
                  className="text-white text-sm font-medium capitalize hover:text-gray-300 transition-colors"
                >
                  {service?.title}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Newsletter/Subscription Section */}
        <div className="mb-5 md:mb-10">
          <h3 className="text-xl font-medium mb-8 text-white">
            {t("sub_title")}
          </h3>
          <WhatsAppNotificationForm />
          <p className="text-white text-lg font-medium mt-4 mb-2">
            {t("pay_method_title")}
          </p>
          <div className="flex items-center gap-2">
            {PaymentOptions.map((image, index: number) => (
              <Image
                key={index}
                src={image}
                alt="payment option image"
                width={40}
                height={20}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Copyright Section */}
      <div className="mt-8 pt-8 border-t border-gray-700">
        <div className="container px-5 mx-auto flex items-center justify-center gap-4 flex-col md:flex-row md:justify-between sm:gap-8">
          <p className="text-white text-center md:text-left text-sm">
            {data?.copyright_note}
          </p>
          <div className="flex items-center gap-4">
            <Link
              href={`tel:${data?.contact_details?.phone_number}`}
              target="_blank"
              className="bg-[#272727] w-6 h-6 flex items-center justify-center rounded-full text-white text-xs hover:bg-[#333] transition-colors"
            >
              <FaPhone />
            </Link>
            <Link
              href={`https://wa.me/${data?.contact_details?.whatsapp_number}`}
              target="_blank"
              className="bg-[#272727] w-6 h-6 flex items-center justify-center rounded-full text-white text-xs hover:bg-[#333] transition-colors"
            >
              <FaWhatsapp />
            </Link>
            <Link
              href={data?.social_links?.instagram_link || ""}
              target="_blank"
              className="bg-[#272727] w-6 h-6 flex items-center justify-center rounded-full text-white text-xs hover:bg-[#333] transition-colors"
            >
              <FaInstagram />
            </Link>
            <Link
              href={data?.social_links?.twitter_link || ""}
              target="_blank"
              className="bg-[#272727] w-6 h-6 flex items-center justify-center rounded-full text-white text-xs hover:bg-[#333] transition-colors"
            >
              <FaTwitter />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
